/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {calculateHeight, errorMsg, isSmWidth} from "../../../../../Apis/functions";
import {GetMaterialControlById} from "../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import routes from "../../../../../Routes/routes";
import {FILE_MODEL} from "../../../Visitor/Visitor/Model/Model";
import {
    getEntryAndExitFormByKey,
    getEntryAndExitFormByType,
    getMovementDirectionOptionByValue,
    getMovementDirectionSection,
    getPendingGateEventTrans,
    MATERIAL_CONTROL_DETAIL_MODEL
} from "../../Model/Model";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";

const MaterialRequestDetailsApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const location = useLocation();
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const { id } = useParams();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    const [modified, setModified] = useState(false);
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [item, setItem] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [evidence, setEvidence] = useState([]);
    // The detail reads the folio with the shape every movement of material
    // control carries, so the blocks the service keeps apart travel with it
    // under the names the sections look for
    const detail = MATERIAL_CONTROL_DETAIL_MODEL.toSet(item);
    const statusId = detail?.dispatchStatusId;

    /**
     * Type of the form the request was registered with.
     *
     * The type is read from the request itself and falls back to the one
     * selected when the user navigates from the list, because the requests
     * registered before the type was captured do not return it.
     */
    const form = item?.type
        ? getEntryAndExitFormByType(item.type)
        : getEntryAndExitFormByKey(location?.state?.formKey);

    // A dispatched folio is closed, and the gate log it was dispatched with is
    // the one that tells it, so only what the gate reported is read above the
    // detail
    const showBadge = statusId === STATUS_MODEL.DISCREPANCY;
    // The gate is what closes the folio, so while it waits for a movement of it
    // the notice of the one it waits for is read instead of a badge
    const pendingGateTrans = getPendingGateEventTrans(statusId);

    /**
     * Direction of the movement and the complementary section it enables.
     *
     * Both are null for the forms that do not capture a direction, so the view
     * only displays them when the movement has one.
     */
    const movementDirectionOption = getMovementDirectionOptionByValue(item?.movementDirection);
    const movementDirectionSection = getMovementDirectionSection(movementDirectionOption?.key);

    /**
     * Effect: Adjusts dynamic container heights based on screen size
     * and dispatch badge visibility.
     *
     * Behavior:
     * - Skips execution on small screen devices.
     * - Dynamically calculates available height for:
     *   - Main card container
     *   - Table container
     *   - Card content area
     *   - Table content area
     * - Applies different bottom spacing depending on whether
     *   the dispatch badge is visible.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = showBadge || statusId === STATUS_MODEL.DENIED || pendingGateTrans ? 80 : 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, [showBadge, statusId, pendingGateTrans]);

    /**
     * Effect: Loads material request details
     * when the component is initialized.
     */
    useEffect(() => {
        getInfo();
    }, []);

    /**
     * Effect: Reloads material request information
     * after a successful modification.
     *
     * Behavior:
     * - Re-fetches request information.
     * - Resets modification state.
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Retrieves material request detail information by identifier.
     *
     * Behavior:
     * - Fetches request information from backend service.
     * - Updates local state with:
     *   - Material request information.
     *   - Associated evidence files.
     * - Maps evidence into FILE_MODEL instances for UI handling.
     * - Displays backend error messages when request fails.
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetMaterialControlById(id)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setItem(data);
                    const aux = data?.evidence?.map(e => FILE_MODEL.set(e));
                    if(aux) setEvidence(aux);
                }else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the view the detail was opened from.
     *
     * The sub view the request was read in travels along with the navigation, so
     * the tab lands on the same list the user left, and falls back to the main
     * view when the detail is opened straight from the url.
     *
     * @returns {void}
     */
    const goBack = () => {
        const backUrl = location?.state?.backUrl;
        navigate(backUrl?.pathname || routes.materialControl.main, {state: backUrl?.state});
    }

    return {
        t,
        navigate,
        DEV,
        permission,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        item: detail,
        goBack,
        evidence,
        form,
        statusId,
        pendingGateTrans,
        movementDirectionOption,
        movementDirectionSection,
        showBadge,
        showReportModal,
        setShowReportModal
    };
};

export default MaterialRequestDetailsApp; // Exporting the component for use in other parts of the app
